import { GodiverseLite } from '@apeiron/godiverse'
import {
  OrbitalSlotType,
  Star,
  StarExplain,
  StarExplainType
} from '@apeiron/star'
import { ApeironTooltip, MuiLoading } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import useGetStar from '@src/hooks/graphql/useGetStar'
import * as R from 'ramda'
import { FC, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const StyledMuiLoading = styled(MuiLoading)`
  margin: 20px 30px;
`

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 5px 7px;
`

const Text = styled(Typography)`
  color: #9e9e9e;
  font-size: 12px;
  font-weight: 300;
  line-height: 12px;
`

const BoldText = styled(Typography)`
  display: inline;
  font-weight: 500;
  margin-left: 2px;
`

const Buff = styled(BoldText)`
  color: #6eae5b;
`

const Equipped = styled(BoldText)`
  color: #f5c154;
`

const Total = styled(BoldText)`
  color: white;
`

const ExplainContent: FC<ExplainContantProps> = props => {
  const { starId } = props

  const { data: star } = useGetStar(starId)

  const { t } = useTranslation()

  const infoList = useMemo((): StarExplain[] => {
    const extractEquippedLength = (
      star: Star | null,
      slotType: OrbitalSlotType
    ): number => {
      const extract = R.pipe<
        any,
        Array<GodiverseLite | null>,
        Array<GodiverseLite>,
        number
      >(
        R.pathOr([], ['slotsMap', slotType, 'equipped']),
        R.reject(R.isNil),
        R.length
      )

      return !R.isNil(star) ? extract(star) : 0
    }

    const extractTotalLength = (
      star: Star | null,
      slotType: OrbitalSlotType
    ): number => {
      return R.pathOr(0, ['slotsMap', slotType, 'count'], star) as number
    }

    return [
      {
        key: 'age_buff',
        type: StarExplainType.Buff,
        text: t('star.card.age_buff', {
          base: star?.buff?.agingBuff || 0,
          addition: star?.additionBuff?.agingBuff || 0
        })
      },
      {
        key: 'track_buff',
        type: StarExplainType.Buff,
        text: t('star.card.track_buff', {
          base: star?.buff?.orbitalTrack || 0,
          addition: star?.additionBuff?.orbitalTrack || 0
        })
      },
      {
        key: 'slots',
        type: StarExplainType.Text,
        text: t('star.card.occupied_slot')
      },
      {
        key: 'slot_sun',
        type: StarExplainType.Slot,
        text: t('star.card.sun', {
          equipped: extractEquippedLength(star, OrbitalSlotType.SunSlot),
          total: extractTotalLength(star, OrbitalSlotType.SunSlot)
        })
      },
      {
        key: 'slot_asteroid',
        type: StarExplainType.Slot,
        text: t('star.card.asteroid', {
          equipped: extractEquippedLength(
            star,
            OrbitalSlotType.AsteroidBeltSlot
          ),
          total: extractTotalLength(star, OrbitalSlotType.AsteroidBeltSlot)
        })
      },
      {
        key: 'slot_comet',
        type: StarExplainType.Slot,
        text: t('star.card.comet', {
          equipped: extractEquippedLength(star, OrbitalSlotType.CometSlot),
          total: extractTotalLength(star, OrbitalSlotType.CometSlot)
        })
      },
      {
        key: 'slot_kuiper',
        type: StarExplainType.Slot,
        text: t('star.card.kuiper', {
          equipped: extractEquippedLength(star, OrbitalSlotType.KuiperBeltSlot),
          total: extractTotalLength(star, OrbitalSlotType.KuiperBeltSlot)
        })
      },
      {
        key: 'slot_supernova',
        type: StarExplainType.Slot,
        text: t('star.card.supernova', {
          equipped: extractEquippedLength(star, OrbitalSlotType.SupernovaSlot),
          total: extractTotalLength(star, OrbitalSlotType.SupernovaSlot)
        })
      }
    ]
  }, [star, t])

  const loading = R.isNil(star)

  return (
    <Container>
      {loading ? (
        <StyledMuiLoading loading />
      ) : (
        <>
          {R.map((info: StarExplain) => {
            switch (info.type) {
              case StarExplainType.Buff:
                return (
                  <Text key={info.key}>
                    <Trans components={{ green: <Buff /> }}>{info.text}</Trans>
                  </Text>
                )

              case StarExplainType.Slot:
                return (
                  <Text key={info.key}>
                    <Trans
                      components={{ orange: <Equipped />, white: <Total /> }}
                    >
                      {info.text || '-'}
                    </Trans>
                  </Text>
                )

              default:
                return <Text key={info.key}>{info.text}</Text>
            }
          }, infoList)}
        </>
      )}
    </Container>
  )
}

const StarCardExplain: FC<Props> = (props: Props) => {
  const { tooltipId, ...rest } = props

  const [show, setShow] = useState<boolean>(false)

  const handleOnShow = useCallback(() => {
    setShow(true)
  }, [])

  const handleOnHide = useCallback(() => {
    setShow(false)
  }, [])

  return (
    <ApeironTooltip
      id={tooltipId}
      afterShow={handleOnShow}
      afterHide={handleOnHide}
    >
      {show ? <ExplainContent {...rest} /> : <></>}
    </ApeironTooltip>
  )
}

type ExplainContantProps = {
  starId: string
}

type Props = {
  starId: string
  tooltipId: string
}

export default StarCardExplain
